import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  EmployeeFicaArgs,
  ReportDownloadArgs,
  ReportGeneratedArgs,
  SortOptions
} from '../../../../models/reports';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PeopleService } from '../../../../services/people.service';
import { Observable, BehaviorSubject, forkJoin } from 'rxjs';
import { PagedPayload } from '../../../../models/paged-payload';
import { Person } from '../../../../models/person';
import { Option } from '../../../../components/multiselect/multiselect.component';
import { CompaniesService } from '../../../../services/companies.service';
import { PayrollTotals } from '../../../../models/client';
import { GetQuarter, GetQuarterEnd, GetQuarterStart } from '../../../../utils/quarter-utils';
import { ReportsService } from '../../../../services/reports.service';
import moment from 'moment';

@Component({
  selector: 'lib-employee-fica',
  templateUrl: './employee-fica.component.html',
  styleUrls: ['./employee-fica.component.css'],
  standalone: false
})
export class EmployeeFicaComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Output()
  public reportGenerated = new EventEmitter<ReportGeneratedArgs>();
  @Output()
  public reportDownloadClicked = new EventEmitter<ReportDownloadArgs>();
  private readonly ENDPOINT = 'EmployeeFica';
  public reportGenerationForm: UntypedFormGroup;
  public sortOptions: SortOptions[];
  public secondSortOptions: string[] = null;
  public people: Person[] = [];
  public peopleNameOpts: Option[] = [];
  public peopleIdOpts: Option[] = [];
  private lastPayrollTotals: any[];
  constructor(
    private fb: UntypedFormBuilder,
    private peopleService: PeopleService,
    private companyService: CompaniesService,
    private reportService: ReportsService
  ) {}

  ngOnInit(): void {
    this.peopleService
      .searchPeople(this.clientCode, { pageNumber: 1, perPage: 100 })
      .subscribe(result => {
        this.people.push(...result.Items);
        if (result.Pagination.Pages > 1) {
          const obs: Observable<PagedPayload<Person>>[] = [];
          for (let i = 2; i <= result.Pagination.Pages; i++) {
            obs.push(
              this.peopleService.searchPeople(this.clientCode, { pageNumber: i, perPage: 100 })
            );
          }
          forkJoin(obs).subscribe((additionalResults: PagedPayload<Person>[]) => {
            additionalResults.forEach(x => this.people.push(...x.Items));
            this._loadPayrollTotals();
          });
        } else {
          this._loadPayrollTotals();
        }
      });
  }
  public generateReport() {
    const args = this._getModel();
    this.reportGenerated.emit({ args, endpoint: this.ENDPOINT });
  }

  public downloadReport(format: string) {
    const args = this._getModel();
    const quarter = this.reportGenerationForm.get('quarter').value;
    const now = moment().format('MMDDYYYY');
    this.reportDownloadClicked.emit({
      args,
      endpoint: this.ENDPOINT,
      filename: `Employee Fica Report_${this.clientCode}_${quarter.year} Q${quarter.quarter}_${now}.${format}`,
      format
    });
  }
  private _getModel() {
    const quarter = this.reportGenerationForm.get('quarter').value;
    const model: EmployeeFicaArgs = {
      EmployeeIds: this.reportGenerationForm.get('employeeIds').value,
      StartDate: GetQuarterStart(quarter.quarter, quarter.year),
      EndDate: GetQuarterEnd(quarter.quarter, quarter.year),
      IncludePickupSuccessorFigures: this.reportGenerationForm.get('includePickupSuccessorFigures')
        .value,
      FirstSortBy: this.reportGenerationForm.get('firstSortBy').value,
      SecondSortBy: this.reportGenerationForm.get('secondSortBy').value
    };
    return model;
  }
  private _loadPayrollTotals() {
    this.companyService.getLastPayrollTotals(this.clientCode, 0).subscribe(x => {
      this.lastPayrollTotals = x;
      this._loadSortOptions();
    });
  }
  private _loadSortOptions() {
    this.reportService.getSortOptions(this.clientCode, this.ENDPOINT).subscribe(opts => {
      this.sortOptions = opts;
      this._initForm();
    });
  }
  private _initForm() {
    this.peopleIdOpts = this.people.map(this._ConvertForNumberList).sort(this._SortPeopleList);
    this.peopleNameOpts = this.people.map(this._ConvertForNameList).sort(this._SortPeopleList);
    const date =
      this.lastPayrollTotals.length > 0
        ? new Date(Date.parse(this.lastPayrollTotals[0].PayDate))
        : new Date();
    this.reportGenerationForm = this.fb.group({
      employeeIds: this.fb.control([], Validators.required),
      quarter: this.fb.control(
        {
          year: date.getFullYear(),
          quarter: GetQuarter(date)
        },
        Validators.required
      ),
      includePickupSuccessorFigures: this.fb.control(false),
      firstSortBy: this.fb.control('Employee Name'),
      secondSortBy: this.fb.control({ value: null, disabled: true })
    });
    this.reportGenerationForm.get('employeeIds').valueChanges.subscribe(value => {
      this.reportGenerationForm.get('employeeIds').setValue(value, {
        onlySelf: true,
        emitEvent: false,
        emitModelToViewChange: true
      });
    });
    this.reportGenerationForm.get('firstSortBy').valueChanges.subscribe(val => {
      const firstOpt = this.sortOptions.find(x => x.FirstSort === val);
      this.secondSortOptions = firstOpt.SecondSortOptions;
      if (this.secondSortOptions == null) {
        this.reportGenerationForm.get('secondSortBy').disable();
      } else {
        this.reportGenerationForm.get('secondSortBy').enable();
      }
    });
  }

  private _ConvertForNumberList(x: Person) {
    return {
      value: x.Id,
      text: x.EmployeeId
    };
  }

  private _ConvertForNameList(x: Person) {
    let middleInitial = '';
    if (x.MiddleName != null && x.MiddleName != '') {
      middleInitial = ` ${x.MiddleName[0]}`;
    }
    return {
      value: x.Id,
      text: `${x.LastName}, ${x.FirstName}${middleInitial}`
    };
  }

  private _SortPeopleList(a: any, b: any) {
    if (a.text < b.text) return -1;

    if (a.text > b.text) return 1;

    return 0;
  }
}
